import {t} from "@lingui/core/macro";
import {StyleSheet, type CSSProperties} from "aphrodite";
import React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {ModalLauncher, OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {
    border,
    semanticColor,
    spacing,
} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";

import QuestionContent from "./question-content.tsx";
import type {BlooketQuestion} from "./types.ts";

import deleteIcon from "@phosphor-icons/core/bold/trash-bold.svg";

interface Props {
    card: BlooketQuestion;
    setCard: (value: BlooketQuestion) => void;
    cardHeight: number;
    cardWidth: number;
    showActions: boolean;
    onDelete: () => void;
    animationStyle: CSSProperties;
}

interface ModalProps {
    onDelete: () => void;
    setDeleteModalIsOpen: (value: boolean) => void;
}

const DeleteConfirmationModal: React.FC<ModalProps> = (props) => {
    const handleDelete = () => {
        props.onDelete();
        props.setDeleteModalIsOpen(false);
    };

    const handleClose = () => {
        props.setDeleteModalIsOpen(false);
    };

    return (
        <OnePaneDialog
            style={styles.deleteConfirmationModal}
            title={t`Are you sure?`}
            content={
                <LabelMedium>
                    {t`Are you sure you you want to delete this question?`}
                </LabelMedium>
            }
            footer={
                <View style={styles.modalFooter}>
                    <Button kind="secondary" onClick={handleClose}>
                        {t`Cancel`}
                    </Button>
                    <Strut size={spacing.small_12} />
                    <Button actionType="destructive" onClick={handleDelete}>
                        {t`Delete question`}
                    </Button>
                </View>
            }
        />
    );
};

const Card: React.FC<Props> = (props) => {
    const [deleteModalIsOpen, setDeleteModalIsOpen] = React.useState(false);
    const cardDimensions = {
        width: props.cardWidth,
        height: props.cardHeight,
    };
    return (
        <View style={[styles.card, props.animationStyle, cardDimensions]}>
            <ModalLauncher
                opened={deleteModalIsOpen}
                modal={
                    <DeleteConfirmationModal
                        onDelete={props.onDelete}
                        setDeleteModalIsOpen={setDeleteModalIsOpen}
                    />
                }
                onClose={() => setDeleteModalIsOpen(false)}
            />
            <View style={styles.questionHeader}>
                {props.showActions && (
                    <View>
                        <IconButton
                            icon={deleteIcon}
                            kind="tertiary"
                            onClick={() => setDeleteModalIsOpen(true)}
                            style={styles.icon}
                        />
                    </View>
                )}
            </View>
            <QuestionContent card={props.card} setCard={props.setCard} />
        </View>
    );
};

const styles = StyleSheet.create({
    card: {
        display: "block",
        overflow: "hidden",
        borderStyle: "solid",
        borderWidth: border.width.thin,
        borderColor: semanticColor.core.border.neutral.subtle,
        borderRadius: border.radius.radius_040,
        backgroundColor: semanticColor.core.background.base.default,
    },
    questionHeader: {
        padding: spacing.xxSmall_6,
        paddingBlockEnd: spacing.xxxSmall_4,
        borderBlockEnd: `thin solid ${semanticColor.core.border.neutral.subtle}`,
        flexDirection: "row",
        justifyContent: "end",
    },
    icon: {
        color: semanticColor.core.foreground.neutral.subtle,
    },
    modalFooter: {
        flexDirection: "row",
        margin: spacing.xSmall_8,
    },
    deleteConfirmationModal: {
        height: 240,
    },
});

export default Card;
